import React, { useState, useRef, useEffect, useCallback } from 'react';

const PEEL_MS = 900;
const RUN_MS = 900;
const OFFSCREEN_MS = 900;
const RETURN_MS = 900;

const skinDefs = (
  <defs>
    <radialGradient id="pot-skin" cx="35%" cy="30%" r="80%">
      <stop offset="0%" stopColor="#D8A97A" />
      <stop offset="55%" stopColor="#A67C52" />
      <stop offset="100%" stopColor="#6E4B2A" />
    </radialGradient>
    <radialGradient id="pot-flesh" cx="35%" cy="30%" r="80%">
      <stop offset="0%" stopColor="#FFF3D6" />
      <stop offset="60%" stopColor="#F1DDAF" />
      <stop offset="100%" stopColor="#D6B67C" />
    </radialGradient>
    <radialGradient id="peel-grad" cx="30%" cy="30%">
      <stop offset="0%" stopColor="#CFA179" />
      <stop offset="100%" stopColor="#6E4B2A" />
    </radialGradient>
    <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="2.5" />
      <feOffset dx="0" dy="3" result="off" />
      <feComponentTransfer><feFuncA type="linear" slope="0.35" /></feComponentTransfer>
      <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
  </defs>
);

const BODY_D = 'M78,150 C58,102 96,54 145,52 C180,50 208,44 236,74 C266,106 274,166 250,214 C226,262 190,286 148,282 C104,278 72,254 62,214 C52,178 58,168 78,150 Z';

const TEXTURE_DOTS = [
  [150,130],[200,170],[120,130],[175,230],[95,165],[220,180],
  [155,200],[130,205],[185,190],[165,115],[105,245],[235,190],[80,185],[250,160]
];

const SkinTexture = () => (
  <g>
    <ellipse cx="112" cy="176" rx="7" ry="4" fill="#3a2210" opacity="0.55" transform="rotate(-25 112 176)" />
    <ellipse cx="112" cy="176" rx="4" ry="2" fill="#1a0f06" opacity="0.7" transform="rotate(-25 112 176)" />
    <ellipse cx="190" cy="96" rx="6" ry="3" fill="#3a2210" opacity="0.55" transform="rotate(20 190 96)" />
    <ellipse cx="210" cy="210" rx="8" ry="4" fill="#3a2210" opacity="0.55" transform="rotate(-10 210 210)" />
    <ellipse cx="140" cy="240" rx="6" ry="3" fill="#3a2210" opacity="0.55" transform="rotate(15 140 240)" />
    <ellipse cx="90" cy="212" rx="5" ry="3" fill="#3a2210" opacity="0.5" transform="rotate(35 90 212)" />
    <ellipse cx="230" cy="140" rx="4" ry="2.5" fill="#3a2210" opacity="0.5" />
    {TEXTURE_DOTS.map(([x,y], i) => (
      <circle key={`dot-${x}-${y}`} cx={x} cy={y} r={0.8+(i%3)*0.4} fill="#3a2210" opacity={0.35+(i%4)*0.08} />
    ))}
    <path d="M95,90 Q118,72 148,80" stroke="#fff" strokeWidth="7" opacity="0.15" strokeLinecap="round" fill="none" />
    <path d="M100,95 Q120,82 140,88" stroke="#fff" strokeWidth="3" opacity="0.25" strokeLinecap="round" fill="none" />
  </g>
);

const PeelStrip = ({ d, className }) => (
  <g className={`peel-strip ${className}`}>
    <path d={d} fill="none" stroke="url(#peel-grad)" strokeWidth="10" strokeLinecap="round" />
    <path d={d} fill="none" stroke="#F1DDAF" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
  </g>
);

const CalmFace = () => (
  <g>
    <circle cx="132" cy="152" r="4.5" fill="#fff" stroke="#2a1608" strokeWidth="1.2" />
    <circle cx="180" cy="150" r="4.5" fill="#fff" stroke="#2a1608" strokeWidth="1.2" />
    <circle cx="133" cy="153" r="2" fill="#1a0f06" />
    <circle cx="181" cy="151" r="2" fill="#1a0f06" />
    <path d="M138,178 Q156,190 174,177" stroke="#2a1608" strokeWidth="2" fill="none" strokeLinecap="round" />
  </g>
);

const ShockedFace = () => (
  <g>
    <circle cx="132" cy="150" r="8" fill="#fff" stroke="#2a1608" strokeWidth="1.5" />
    <circle cx="180" cy="148" r="8" fill="#fff" stroke="#2a1608" strokeWidth="1.5" />
    <circle cx="133" cy="152" r="3.6" fill="#1a0f06" />
    <circle cx="181" cy="150" r="3.6" fill="#1a0f06" />
    <path d="M120,132 Q132,126 144,132" stroke="#2a1608" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <path d="M168,130 Q180,124 192,130" stroke="#2a1608" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <ellipse cx="156" cy="188" rx="9" ry="13" fill="#2a1608" />
    <ellipse cx="156" cy="192" rx="6" ry="7" fill="#8B3A2F" />
    <ellipse cx="102" cy="180" rx="12" ry="7" fill="#FF6B6B" opacity="0.55" />
    <ellipse cx="212" cy="178" rx="12" ry="7" fill="#FF6B6B" opacity="0.55" />
  </g>
);

const RunningFace = () => (
  <g>
    <ellipse cx="132" cy="150" rx="6" ry="7" fill="#fff" stroke="#2a1608" strokeWidth="1.5" />
    <ellipse cx="180" cy="148" rx="6" ry="7" fill="#fff" stroke="#2a1608" strokeWidth="1.5" />
    <circle cx="127" cy="153" r="2.6" fill="#1a0f06" />
    <circle cx="175" cy="151" r="2.6" fill="#1a0f06" />
    <path d="M120,134 Q132,140 146,134" stroke="#2a1608" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M168,132 Q180,138 192,132" stroke="#2a1608" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M138,188 L174,186" stroke="#2a1608" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M142,184 L142,192 M150,184 L150,192 M158,184 L158,192 M166,184 L166,192 M170,184 L170,192" stroke="#2a1608" strokeWidth="1" />
    <path d="M220,120 Q225,138 220,146 Q215,138 220,120 Z" fill="#7CC7F0" stroke="#2a1608" strokeWidth="1.2" />
    <ellipse cx="102" cy="180" rx="10" ry="6" fill="#FFB4B4" opacity="0.7" />
    <ellipse cx="212" cy="178" rx="10" ry="6" fill="#FFB4B4" opacity="0.7" />
  </g>
);

const SheepishFace = () => (
  <g>
    <path d="M124,150 Q132,155 140,150" stroke="#2a1608" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <path d="M172,148 Q180,153 188,148" stroke="#2a1608" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <path d="M138,180 Q156,195 174,180" stroke="#2a1608" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    <ellipse cx="102" cy="180" rx="14" ry="8" fill="#FF6B6B" opacity="0.55" />
    <ellipse cx="212" cy="178" rx="14" ry="8" fill="#FF6B6B" opacity="0.55" />
  </g>
);

const Legs = ({ running }) => (
  <g>
    <path d="M118,278 L108,315" stroke="#2a1608" strokeWidth="6" strokeLinecap="round" className={running ? 'leg-l' : ''} />
    <path d="M172,278 L184,315" stroke="#2a1608" strokeWidth="6" strokeLinecap="round" className={running ? 'leg-r' : ''} />
    <ellipse cx="104" cy="317" rx="14" ry="6" fill="#2a1608" className={running ? 'foot-l' : ''} />
    <ellipse cx="188" cy="319" rx="14" ry="6" fill="#2a1608" className={running ? 'foot-r' : ''} />
  </g>
);

const Arms = ({ mode }) => {
  if (mode === 'shocked') {
    return (
      <g>
        <path d="M78,180 Q90,220 140,230" stroke="#2a1608" strokeWidth="5" fill="none" strokeLinecap="round" />
        <path d="M240,180 Q228,220 178,230" stroke="#2a1608" strokeWidth="5" fill="none" strokeLinecap="round" />
        <circle cx="140" cy="230" r="8" fill="url(#pot-flesh)" stroke="#2a1608" strokeWidth="2" />
        <circle cx="178" cy="230" r="8" fill="url(#pot-flesh)" stroke="#2a1608" strokeWidth="2" />
      </g>
    );
  }
  if (mode === 'running') {
    return (
      <g>
        <path d="M72,175 Q40,205 30,240" stroke="#2a1608" strokeWidth="5" fill="none" strokeLinecap="round" className="arm-l" />
        <path d="M244,175 Q276,205 286,240" stroke="#2a1608" strokeWidth="5" fill="none" strokeLinecap="round" className="arm-r" />
        <circle cx="30" cy="240" r="9" fill="url(#pot-flesh)" stroke="#2a1608" strokeWidth="2.5" className="arm-l" />
        <circle cx="286" cy="240" r="9" fill="url(#pot-flesh)" stroke="#2a1608" strokeWidth="2.5" className="arm-r" />
      </g>
    );
  }
  return (
    <g>
      <path d="M78,175 Q52,180 44,155" stroke="#2a1608" strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M240,175 Q266,180 274,155" stroke="#2a1608" strokeWidth="5" fill="none" strokeLinecap="round" />
      <circle cx="44" cy="155" r="9" fill="url(#pot-skin)" stroke="#2a1608" strokeWidth="2.5" />
      <circle cx="274" cy="155" r="9" fill="url(#pot-skin)" stroke="#2a1608" strokeWidth="2.5" />
    </g>
  );
};

const ShockedOverlays = () => (
  <g>
    <PeelStrip d="M90,80 C60,60 40,50 20,60" className="peel-a" />
    <PeelStrip d="M240,80 C270,60 290,55 305,65" className="peel-b" />
    <PeelStrip d="M60,180 C30,170 15,190 5,220" className="peel-c" />
    <PeelStrip d="M260,180 C295,175 305,200 310,225" className="peel-d" />
    <g className="crumb crumb-a"><ellipse cx="60" cy="100" rx="4" ry="3" fill="#6E4B2A" /></g>
    <g className="crumb crumb-b"><ellipse cx="260" cy="110" rx="5" ry="3" fill="#6E4B2A" /></g>
    <g className="crumb crumb-c"><ellipse cx="285" cy="200" rx="3" ry="2.5" fill="#A67C52" /></g>
  </g>
);

const RunningOverlays = () => (
  <>
    <g className="speed-lines">
      <line x1="-40" y1="140" x2="10" y2="140" stroke="#2a1608" strokeWidth="3" strokeLinecap="round" />
      <line x1="-30" y1="170" x2="20" y2="170" stroke="#2a1608" strokeWidth="3" strokeLinecap="round" />
      <line x1="-50" y1="200" x2="0" y2="200" stroke="#2a1608" strokeWidth="3" strokeLinecap="round" />
      <line x1="-35" y1="230" x2="15" y2="230" stroke="#2a1608" strokeWidth="3" strokeLinecap="round" />
    </g>
    <g className="dust">
      <circle cx="60" cy="315" r="10" fill="#EDD9B3" opacity="0.85" />
      <circle cx="75" cy="322" r="7" fill="#EDD9B3" opacity="0.7" />
      <circle cx="45" cy="322" r="8" fill="#EDD9B3" opacity="0.7" />
    </g>
  </>
);

const FACES = { idle: <CalmFace />, shocked: <ShockedFace />, running: <RunningFace />, returning: <SheepishFace /> };
const CHIPS = { idle: 'touch me!', shocked: 'AAAH!', running: 'brb!', returning: 'sorry…' };

const usePotatoTimeline = () => {
  const [mode, setMode] = useState('idle');
  const [showHint, setShowHint] = useState(true);
  const timers = useRef([]);

  const clearAll = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const trigger = useCallback(() => {
    if (mode !== 'idle') return;
    setShowHint(false);
    clearAll();
    setMode('shocked');
    timers.current.push(setTimeout(() => setMode('running'), PEEL_MS));
    timers.current.push(setTimeout(() => setMode('returning'), PEEL_MS + RUN_MS + OFFSCREEN_MS));
    timers.current.push(setTimeout(() => setMode('idle'), PEEL_MS + RUN_MS + OFFSCREEN_MS + RETURN_MS));
  }, [mode, clearAll]);

  useEffect(() => {
    return () => clearAll();
  }, [clearAll]);

  return { mode, showHint, trigger };
};

const InteractivePotato = () => {
  const { mode, showHint, trigger } = usePotatoTimeline();
  const peeled = mode !== 'idle';
  const face = FACES[mode];
  const chip = CHIPS[mode];

  return (
    <div className="relative select-none" style={{ touchAction: 'manipulation' }}>
      <div className="relative w-[280px] h-[340px] sm:w-[340px] sm:h-[400px] md:w-[420px] md:h-[480px] lg:w-[460px] lg:h-[520px] mx-auto overflow-hidden">
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-3/5 h-3 rounded-full bg-black/25 blur-md" />

        <div
          onClick={trigger}
          role="button"
          aria-label={`Potato is ${mode}. Touch to peel it.`}
          className={`absolute inset-0 cursor-pointer ${mode === 'idle' ? 'float-slow' : ''} ${mode}-wrap`}
        >
          <svg viewBox="0 0 320 340" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            {skinDefs}
            {mode === 'shocked' && <ShockedOverlays />}
            <g filter="url(#soft-shadow)">
              <path d={BODY_D} fill={peeled ? 'url(#pot-flesh)' : 'url(#pot-skin)'} stroke="#2a1608" strokeWidth="3" />
              {!peeled && <SkinTexture />}
              {peeled && (
                <>
                  <path d="M95,90 Q120,72 150,82" stroke="#fff" strokeWidth="10" opacity="0.35" strokeLinecap="round" fill="none" />
                  <path d="M100,100 Q120,88 145,94" stroke="#fff" strokeWidth="4" opacity="0.55" strokeLinecap="round" fill="none" />
                  <ellipse cx="215" cy="230" rx="18" ry="10" fill="#A67C52" opacity="0.35" transform="rotate(20 215 230)" />
                  <ellipse cx="90" cy="150" rx="10" ry="6" fill="#A67C52" opacity="0.35" transform="rotate(-20 90 150)" />
                </>
              )}
            </g>
            <Arms mode={mode === 'returning' ? 'idle' : mode} />
            <Legs running={mode === 'running'} />
            {face}
            {mode === 'running' && <RunningOverlays />}
          </svg>
        </div>

        <div className="absolute top-1 right-1 md:top-2 md:right-2 bg-white border-2 border-black rounded-full px-3 py-1 text-[10px] md:text-xs font-mono font-bold shadow-[3px_3px_0_#111] z-10">
          {chip}
        </div>

        {showHint && mode === 'idle' && (
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-black text-[#FFD200] rounded-full px-3 py-1.5 text-[10px] md:text-xs font-mono font-bold shadow-[3px_3px_0_#fff] whitespace-nowrap animate-bounce z-10">
            go on, give me a poke
          </div>
        )}
      </div>

      <style>{`
        .shocked-wrap { animation: tremor 0.6s ease-in-out; }
        @keyframes tremor {
          0%,100% { transform: translate(0,0) rotate(0); }
          20%  { transform: translate(-3px, 2px) rotate(-2deg); }
          40%  { transform: translate(3px, -2px) rotate(2deg); }
          60%  { transform: translate(-2px, 1px) rotate(-1deg); }
          80%  { transform: translate(2px, -1px) rotate(1deg); }
        }
        .peel-strip { transform-origin: center; opacity: 1; }
        .peel-strip.peel-a { animation: peel-out-a ${PEEL_MS}ms cubic-bezier(.2,.7,.3,1) forwards; }
        .peel-strip.peel-b { animation: peel-out-b ${PEEL_MS}ms cubic-bezier(.2,.7,.3,1) forwards; }
        .peel-strip.peel-c { animation: peel-out-c ${PEEL_MS}ms cubic-bezier(.2,.7,.3,1) forwards; }
        .peel-strip.peel-d { animation: peel-out-d ${PEEL_MS}ms cubic-bezier(.2,.7,.3,1) forwards; }
        @keyframes peel-out-a { 0% { transform: translate(0,0) rotate(0); opacity: 1; } 100% { transform: translate(-90px, -60px) rotate(-140deg); opacity: 0; } }
        @keyframes peel-out-b { 0% { transform: translate(0,0) rotate(0); opacity: 1; } 100% { transform: translate(90px, -70px) rotate(140deg); opacity: 0; } }
        @keyframes peel-out-c { 0% { transform: translate(0,0) rotate(0); opacity: 1; } 100% { transform: translate(-110px, 40px) rotate(-160deg); opacity: 0; } }
        @keyframes peel-out-d { 0% { transform: translate(0,0) rotate(0); opacity: 1; } 100% { transform: translate(110px, 60px) rotate(160deg); opacity: 0; } }
        .crumb { opacity: 1; }
        .crumb-a { animation: crumb-a ${PEEL_MS}ms ease-out forwards; }
        .crumb-b { animation: crumb-b ${PEEL_MS}ms ease-out forwards; }
        .crumb-c { animation: crumb-c ${PEEL_MS}ms ease-out forwards; }
        @keyframes crumb-a { to { transform: translate(-40px,-90px) rotate(120deg); opacity: 0; } }
        @keyframes crumb-b { to { transform: translate(50px,-100px) rotate(-140deg); opacity: 0; } }
        @keyframes crumb-c { to { transform: translate(70px,20px) rotate(90deg); opacity: 0; } }
        .running-wrap { animation: run-off ${RUN_MS}ms cubic-bezier(.5,0,.7,.4) forwards; }
        @keyframes run-off {
          0%   { transform: translate(0,0) rotate(0); }
          15%  { transform: translate(-8px,-4px) rotate(-3deg); }
          100% { transform: translate(140%,10px) rotate(6deg); }
        }
        .running-wrap .leg-l { animation: pump-l 0.2s ease-in-out infinite; transform-origin: 118px 278px; }
        .running-wrap .leg-r { animation: pump-r 0.2s ease-in-out infinite; transform-origin: 172px 278px; }
        .running-wrap .foot-l { animation: pump-l 0.2s ease-in-out infinite; transform-origin: 104px 317px; }
        .running-wrap .foot-r { animation: pump-r 0.2s ease-in-out infinite; transform-origin: 188px 319px; }
        @keyframes pump-l { 0%,100% { transform: rotate(-15deg); } 50% { transform: rotate(20deg); } }
        @keyframes pump-r { 0%,100% { transform: rotate(20deg); } 50% { transform: rotate(-15deg); } }
        .running-wrap .arm-l { animation: swing-l 0.2s ease-in-out infinite; transform-origin: 72px 175px; }
        .running-wrap .arm-r { animation: swing-r 0.2s ease-in-out infinite; transform-origin: 244px 175px; }
        @keyframes swing-l { 0%,100% { transform: rotate(-15deg); } 50% { transform: rotate(15deg); } }
        @keyframes swing-r { 0%,100% { transform: rotate(15deg); } 50% { transform: rotate(-15deg); } }
        .speed-lines line { animation: speed 0.35s linear infinite; }
        .speed-lines line:nth-child(2) { animation-delay: -0.08s; }
        .speed-lines line:nth-child(3) { animation-delay: -0.16s; }
        .speed-lines line:nth-child(4) { animation-delay: -0.24s; }
        @keyframes speed { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(20px); } }
        .dust circle { animation: dust-puff 0.5s ease-out infinite; }
        .dust circle:nth-child(2) { animation-delay: 0.1s; }
        .dust circle:nth-child(3) { animation-delay: 0.2s; }
        @keyframes dust-puff { 0%{ transform: scale(0.5); opacity: 0.9; } 100%{ transform: scale(1.4); opacity: 0; } }
        .returning-wrap { transform: translate(-140%, 10px); animation: run-back ${RETURN_MS}ms cubic-bezier(.2,.9,.3,1) forwards; }
        @keyframes run-back {
          0%   { transform: translate(-140%, 10px) rotate(-6deg); }
          70%  { transform: translate(8px, -4px) rotate(3deg); }
          100% { transform: translate(0,0) rotate(0); }
        }
      `}</style>
    </div>
  );
};

export default InteractivePotato;
